"use client";

import { track } from "@vercel/analytics";
import { useEffect, useRef, useState } from "react";

import {
  TurnstileCheck,
  TurnstileScript,
  turnstileNotReadyMessage,
  useTurnstile,
} from "@/components/ownly/TurnstileWidget";

import { campaignFields } from "@/lib/campaign-source";
import { AUDIT_GROUPS, type AuditGroupKey, type AuditResult } from "@/lib/site-audit-types";

/**
 * The free audit form, and the report it renders in place.
 *
 * Two deliberate decisions:
 *
 * 1. THE RESULT IS SHOWN ON THE PAGE, not only emailed. Making somebody wait
 *    for an inbox to prove you were worth the email address is how a lead
 *    magnet feels like a bait. They get it immediately and a copy by email.
 * 2. THE EMAIL IS ASKED FOR UP FRONT, with the checks running after. Asking
 *    afterwards reads as a toll gate on something they can already see.
 *
 * Anti-bot is the same Turnstile widget the other public forms use, plus a
 * honeypot field a real person never sees.
 */

const inputClass =
  "w-full rounded-2xl border border-white/[0.14] bg-ownly-surface/90 px-4 py-3.5 text-copy text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] outline-none backdrop-blur-md transition-[border-color,box-shadow] placeholder:text-white/60 focus:border-ownly-accent/55 focus:ring-2 focus:ring-ownly-accent/22";

const STATUS_STYLE: Record<string, { dot: string; label: string; text: string }> = {
  fail: { dot: "bg-[#E05A45]", label: "Fix", text: "text-[#F09A8B]" },
  warn: { dot: "bg-[#D9A441]", label: "Look at", text: "text-[#E5C077]" },
  pass: { dot: "bg-[#5FC28E]", label: "Good", text: "text-[#8FD8B2]" },
  info: { dot: "bg-white/40", label: "Note", text: "text-white/60" },
};

const ORDER: Record<string, number> = { fail: 0, warn: 1, info: 2, pass: 3 };

/**
 * The web address to start the form with, from `?url=` on the page.
 *
 * Our cold outreach names the recipient's own site and then, at the third and fourth email, points
 * them here. Making them retype an address we already know is friction on the exact step we are
 * asking them to take, so the link carries it.
 *
 * Applied after mount rather than as the initial state. The server renders this field empty because
 * it cannot see the query string, so seeding the state directly would render one value on the
 * server and another on the client, and React would complain about the mismatch.
 *
 * Only the shape is trusted here: anything odd is dropped and they get an empty field rather than a
 * prefilled wrong one. The server re-validates regardless, in `assertPublicUrl`, which is what
 * actually keeps this endpoint safe.
 */
function initialUrlFromQuery(): string {
  if (typeof window === "undefined") return "";
  const raw = new URLSearchParams(window.location.search).get("url")?.trim();
  if (!raw || raw.length > 255) return "";
  const withoutScheme = raw.replace(/^https?:\/\//i, "");
  return /^[a-z0-9.-]+\.[a-z]{2,}(\/[^\s]*)?$/i.test(withoutScheme) ? withoutScheme : "";
}

export function SiteAuditForm() {
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AuditResult | null>(null);
  const turnstile = useTurnstile();

  const resultRef = useRef<HTMLDivElement>(null);

  /* Prefill from ?url= once, on mount. Never overwrites something already typed. */
  useEffect(() => {
    const fromQuery = initialUrlFromQuery();
    if (fromQuery) setUrl((current) => (current ? current : fromQuery));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (!url.trim()) return setError("Enter your website address to get started.");
    if (!email.trim()) return setError("We need an email address to send the report to.");
    if (!turnstile.ready) return setError(turnstileNotReadyMessage(turnstile.status));

    setBusy(true);
    setResult(null);
    try {
      const res = await fetch("/api/audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          email,
          name,
          company,
          marketingOptIn: optIn,
          turnstileToken: turnstile.token,
          // Which post or profile link sent them, when they arrived through one of ours.
          ...campaignFields(),
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setError(
          data?.message ??
            "Something went wrong running that check. Please try again in a moment.",
        );
        return;
      }
      setResult(data.result as AuditResult);
      track("audit-completed");
      requestAnimationFrame(() =>
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    } catch {
      setError("We could not reach the checker. Check your connection and try again.");
    } finally {
      setBusy(false);
      turnstile.reset();
    }
  }

  const groups = result
    ? (Object.keys(AUDIT_GROUPS) as AuditGroupKey[])
        .map((key) => ({
          key,
          meta: AUDIT_GROUPS[key],
          items: result.checks
            .filter((c) => c.group === key)
            .sort((a, b) => ORDER[a.status] - ORDER[b.status]),
        }))
        .filter((g) => g.items.length > 0)
        .sort((a, b) => {
          const worst = (g: (typeof a)) => Math.min(...g.items.map((i) => ORDER[i.status]));
          return worst(a) - worst(b);
        })
    : [];

  return (
    <>
      <TurnstileScript />

      <form onSubmit={submit}>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="md:col-span-2">
            <span className="mb-2 block text-sm font-medium text-white/72">
              Your website address
            </span>
            <input
              className={inputClass}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="yourbusiness.co.uk"
              inputMode="url"
              autoComplete="url"
              required
            />
          </label>
          <label>
            <span className="mb-2 block text-sm font-medium text-white/72">
              Where should we send it
            </span>
            <input
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbusiness.co.uk"
              type="email"
              autoComplete="email"
              required
            />
          </label>
          <label>
            <span className="mb-2 block text-sm font-medium text-white/72">
              Your name <span className="text-white/60">(optional)</span>
            </span>
            <input
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First name"
              autoComplete="given-name"
            />
          </label>
        </div>

        {/* Honeypot. Hidden from people, irresistible to bots. */}
        <div aria-hidden className="absolute left-[-9999px] top-0 h-0 w-0 overflow-hidden">
          <label>
            Company
            <input
              tabIndex={-1}
              autoComplete="off"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </label>
        </div>

        <label className="mt-5 flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 rounded border-white/25 bg-white/10 accent-ownly-accent"
          />
          <span className="ownly-prose text-body leading-[27px] text-white/85">
            Send me the follow-up: three short emails over the next fortnight
            about what the report found and how to fix it. Then we stop. One
            click to unsubscribe at any point.
          </span>
        </label>

        <TurnstileCheck turnstile={turnstile} className="mt-5" />

        {error ? (
          <p
            role="alert"
            className="ownly-body mt-5 rounded-xl border border-[#E05A45]/35 bg-[#E05A45]/[0.1] px-4 py-3 leading-[27px] text-[#F2AA9D]"
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy || !turnstile.ready}
          className="ownly-control ownly-btn-primary mt-6 inline-flex h-12 w-full items-center justify-center rounded-full bg-ownly-accent px-7 text-white shadow-[var(--ownly-cta-shadow)] transition-[box-shadow,transform,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#3d8aff] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {busy
            ? "Checking your site…"
            : turnstile.waiting
              ? "Waiting for the security check"
              : "Audit my site"}
        </button>
        <p className="mt-3 text-sm text-white/60">
          Takes about twenty seconds. We check the live page, we do not store a
          copy of your site, and there is no call unless you ask for one. Your
          details are handled as set out in our{" "}
          <a
            href="/privacy"
            className="underline decoration-white/25 underline-offset-2 transition hover:text-white/70"
          >
            privacy policy
          </a>
          .
        </p>
      </form>

      <div ref={resultRef} className="scroll-mt-28 md:scroll-mt-32">
        {result ? (
          <section className="mt-10" aria-label="Your audit results">
            <div>
              <p className="text-sm font-semibold tracking-[0.01em] text-ownly-accent-text">
                {new URL(result.finalUrl).hostname.replace(/^www\./, "")}
              </p>
              <h2 className="ownly-display ownly-section-title mt-3 text-white">
                {result.headline}.
              </h2>
              <dl className="mt-6 flex flex-wrap gap-x-9 gap-y-4">
                {[
                  { n: result.counts.fail, l: "to fix", c: "text-[#F09A8B]" },
                  { n: result.counts.warn, l: "worth a look", c: "text-[#E5C077]" },
                  { n: result.counts.pass, l: "already good", c: "text-[#8FD8B2]" },
                ].map((s) => (
                  <div key={s.l}>
                    <dt className="sr-only">{s.l}</dt>
                    <dd>
                      <span
                        className={`ownly-display text-3xl ${s.c}`}
                        style={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {s.n}
                      </span>
                      <span className="ml-2 text-copy text-white/58">{s.l}</span>
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="ownly-body mt-5 border-t border-white/[0.08] pt-5 leading-[27px] text-white/85">
                A copy is on its way to your inbox. Everything below was measured
                against your live homepage just now, so there is no score out of a
                hundred and nothing invented to pad it out.
              </p>
            </div>

            <div className="mt-8 space-y-10">
              {groups.map((g) => (
                <div key={g.key}>
                  <div className="flex items-baseline justify-between gap-6 border-b border-white/[0.14] pb-3">
                    <h3 className="ownly-display ownly-card-title text-white">
                      {g.meta.name}
                    </h3>
                    <p className="shrink-0 text-sm text-white/60">{g.meta.blurb}</p>
                  </div>
                  <ul>
                    {g.items.map((c) => (
                      <li
                        key={c.id}
                        className="flex gap-3 border-b border-white/[0.07] py-4"
                      >
                        <span
                          className={`mt-[0.5em] size-2 shrink-0 rounded-full ${STATUS_STYLE[c.status].dot}`}
                          aria-hidden
                        />
                        <div>
                          <h4 className="ownly-display ownly-small-title text-white">
                            <span className="sr-only">
                              {STATUS_STYLE[c.status].label}:{" "}
                            </span>
                            {c.title}
                          </h4>
                          <p className="ownly-body mt-1.5 leading-[27px] text-white/85">
                            {c.detail}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
